import { apiClient } from "@/lib/apiClient";

const ADMIN_EDUCATION_VIDEOS_ENDPOINT = "/api/admin/education-videos";

export interface EducationVideoFile {
  id: string;
  filename?: string;
  originalName?: string;
  mimeType?: string;
  size?: number;
  durationMinutes?: number | null;
  createdAt?: string;
}

export interface EducationVideo {
  id: string;
  name: string;
  fileID: string;
  createdAt: string;
  updatedAt: string;
  file?: EducationVideoFile | null;
}

export interface EducationVideoListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface EducationVideoListResponse {
  items: EducationVideo[];
  count: number;
  meta?: EducationVideoListMeta;
}

interface ApiEnvelope<T> {
  data?: T;
}

function unwrap<T>(payload: T | ApiEnvelope<T>): T {
  if (payload && typeof payload === "object" && "data" in payload) {
    const data = payload.data;
    if (data !== undefined) return data;
  }
  return payload as T;
}

function normalizeList(
  payload:
    | EducationVideoListResponse
    | EducationVideo[]
    | ApiEnvelope<EducationVideoListResponse | EducationVideo[]>,
): EducationVideoListResponse {
  const data = unwrap(payload);
  if (Array.isArray(data)) {
    return { items: data, count: data.length };
  }
  return {
    items: data.items ?? [],
    count: data.count ?? data.items?.length ?? 0,
    meta: data.meta,
  };
}

export const educationVideosApi = {
  list: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<EducationVideoListResponse> => {
    const { data } = await apiClient.get<
      | EducationVideoListResponse
      | EducationVideo[]
      | ApiEnvelope<EducationVideoListResponse | EducationVideo[]>
    >(ADMIN_EDUCATION_VIDEOS_ENDPOINT, {
      params,
    });
    return normalizeList(data);
  },

  create: async (body: { name: string; fileID: string }): Promise<EducationVideo> => {
    const { data } = await apiClient.post<EducationVideo | ApiEnvelope<EducationVideo>>(
      ADMIN_EDUCATION_VIDEOS_ENDPOINT,
      body,
    );
    return unwrap(data);
  },

  update: async (
    id: string,
    body: { name?: string; fileID?: string },
  ): Promise<EducationVideo> => {
    const { data } = await apiClient.patch<EducationVideo | ApiEnvelope<EducationVideo>>(
      `${ADMIN_EDUCATION_VIDEOS_ENDPOINT}/${id}`,
      body,
    );
    return unwrap(data);
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${ADMIN_EDUCATION_VIDEOS_ENDPOINT}/${id}`);
  },
};
